import type { z } from 'zod';
import {
  fixtureCollectionSchema,
  interviewBankSchema,
  manifestSchema,
  orientationSchema,
  weekSchema,
  type FixtureCollection,
  type InterviewBank,
  type Manifest,
  type Orientation,
  type Week
} from '../../scripts/content-schema';

const requestCache = new Map<string, Promise<unknown>>();

function dataUrl(file: string): string {
  const base = import.meta.env.BASE_URL.endsWith('/')
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  return `${base}data/${file}`;
}

async function fetchValidated<T>(file: string, schema: z.ZodType<T>): Promise<T> {
  const url = dataUrl(file);
  const cached = requestCache.get(url) as Promise<T> | undefined;

  if (cached) {
    return cached;
  }

  const request = fetch(url)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Could not load ${file} (${response.status}).`);
      }
      return schema.parse(await response.json());
    })
    .catch((error: unknown) => {
      requestCache.delete(url);
      throw error;
    });

  requestCache.set(url, request);
  return request;
}

export function loadManifest(): Promise<Manifest> {
  return fetchValidated('manifest.json', manifestSchema);
}

export function loadInterviewBank(): Promise<InterviewBank> {
  return fetchValidated('interview-bank.json', interviewBankSchema);
}

export function loadOrientation(): Promise<Orientation> {
  return fetchValidated('orientation.json', orientationSchema);
}

export function loadFixtures(): Promise<FixtureCollection> {
  return fetchValidated('fixtures.json', fixtureCollectionSchema);
}

export function loadWeek(manifest: Manifest, weekId: string): Promise<Week> {
  const week = manifest.weeks.find((candidate) => candidate.id === weekId);
  if (!week) {
    return Promise.reject(new Error(`Unknown week: ${weekId}`));
  }
  return fetchValidated(week.file, weekSchema);
}

export function clearRequestCache(): void {
  requestCache.clear();
}

export type {
  ContentBlock,
  ContentDocument,
  Day,
  FixtureCollection,
  InterviewBank,
  InterviewQuestion,
  Manifest,
  Orientation,
  ReflectionField,
  Week
} from '../../scripts/content-schema';
